import React, { useState, useEffect } from 'react';
import {
  FileText, Trash2, Download, RefreshCw, Eye, Zap, Target, Loader2, ExternalLink,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { OrchestratedProposalGenerator } from '../OrchestratedProposalGenerator';
import { ProposalExportService } from '../../services/ProposalExportService';
import toast from 'react-hot-toast';

interface ProposalTemplate {
  id: string;
  template_name: string;
  notice_id: string;
  content_json: any;
  template_url?: string;
  proposal_type: 'basic' | 'enhanced' | 'source-grounded';
  created_at: string;
  updated_at: string;
  contract?: any;
}

// Classify a stored proposal for the badge. Source-grounded / orchestrated proposals
// carry deliverablesData or a redTeamAssessment; enhanced ones carry contractAnalysis.
function classify(t: any): 'basic' | 'enhanced' | 'source-grounded' {
  const c = t.content_json || {};
  if (
    c.proposalType === 'source-grounded' ||
    c.sourceGroundedOutput ||
    c.deliverablesData ||
    c.redTeamAssessment ||
    t.template_name?.includes('Source-Grounded')
  ) return 'source-grounded';
  if (t.proposal_type === 'enhanced' || c.contractAnalysis || t.template_name?.includes('Enhanced')) return 'enhanced';
  return 'basic';
}

export function ProposalTemplates() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<ProposalTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [regenContract, setRegenContract] = useState<any>(null);
  const [showExportMenu, setShowExportMenu] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [openingId, setOpeningId] = useState<string | null>(null);

  useEffect(() => {
    loadTemplates();
  }, [profile]);

  const loadTemplates = async () => {
    try {
      if (!profile) return;
      setLoading(true);

      const { data: templateData, error: templateError } = await supabase
        .from('proposal_templates')
        .select('*')
        .eq('profile_id', profile.id)
        .order('created_at', { ascending: false });
      if (templateError) throw templateError;

      const valid = (templateData || []).filter((t) => t.notice_id);
      const noticeIds = [...new Set(valid.map((t) => t.notice_id))];

      // Batch-load contracts (was an N+1 + silent-drop bug). Look up by notice_id, then
      // by id for anything unmatched. Never drop a proposal: fall back to the contract
      // stored in content_json, then to a minimal record from the template itself.
      const byKey: Record<string, any> = {};
      if (noticeIds.length) {
        const { data: byNotice } = await supabase
          .from('sam_contracts').select('*').in('notice_id', noticeIds);
        for (const c of byNotice || []) byKey[c.notice_id] = c;
        const stillMissing = noticeIds.filter((id) => !byKey[id]);
        if (stillMissing.length) {
          const { data: byId } = await supabase
            .from('sam_contracts').select('*').in('id', stillMissing);
          for (const c of byId || []) byKey[c.id] = c;
        }
      }

      const combined: ProposalTemplate[] = valid.map((t) => {
        const contract =
          byKey[t.notice_id] ||
          t.content_json?.contract || {
            notice_id: t.notice_id,
            title: t.content_json?.contract?.title || t.template_name,
            agency: t.content_json?.contract?.agency || '',
          };
        return { ...t, proposal_type: classify(t), contract };
      });

      setTemplates(combined);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };

  // Open a proposal in the single canonical viewer (the full-page Workspace) —
  // same destination the dashboard uses, with pricing visuals, Recompute Pricing,
  // Build Handoff, Red Team review, Gap Report, and per-section editing.
  const handleView = (template: ProposalTemplate) => {
    navigate(`/workspace/${template.id}`);
  };

  // Resolve the template id for a notice and open it in the Workspace.
  const openWorkspaceForNotice = async (noticeId: string) => {
    if (!profile) return;
    const { data } = await supabase
      .from('proposal_templates')
      .select('id')
      .eq('profile_id', profile.id)
      .eq('notice_id', noticeId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (data?.id) navigate(`/workspace/${data.id}`);
  };

  const handleRegenerate = (template: ProposalTemplate) => {
    if (!template.contract) {
      toast.error('Missing contract data — cannot regenerate.');
      return;
    }
    setRegenContract(template.contract);
  };

  const handleRemove = async (templateId: string) => {
    if (!confirm('Delete this proposal? This cannot be undone.')) return;
    try {
      const { error } = await supabase.from('proposal_templates').delete().eq('id', templateId);
      if (error) throw error;
      setTemplates((prev) => prev.filter((t) => t.id !== templateId));
      toast.success('Proposal deleted');
    } catch (error) {
      console.error('Error deleting proposal:', error);
      toast.error('Failed to delete proposal');
    }
  };

  const handleExport = async (template: ProposalTemplate, format: 'pdf' | 'word' | 'sections' | 'markdown') => {
    if (!template.content_json) {
      toast.error('Cannot export: missing proposal content');
      return;
    }
    try {
      setExporting(true);
      setShowExportMenu(null);
      const contract = template.contract || { title: template.template_name, notice_id: template.notice_id };
      switch (format) {
        case 'pdf':
          await ProposalExportService.exportToPDF(template.content_json, contract);
          break;
        case 'word':
          await ProposalExportService.exportToWord(template.content_json, contract);
          break;
        case 'sections':
          await ProposalExportService.exportSections(template.content_json, contract);
          break;
        case 'markdown':
          await ProposalExportService.exportToMarkdown(template.content_json, contract);
          break;
      }
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error: any) {
      console.error('Error exporting proposal:', error);
      toast.error(`Export failed: ${error?.message || 'unknown error'}`);
    } finally {
      setExporting(false);
    }
  };

  const thisMonth = templates.filter((t) => {
    const d = new Date(t.created_at);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;
  const groundedCount = templates.filter((t) => t.proposal_type === 'source-grounded').length;

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-20 bg-bw-surface-subtle rounded-lg animate-pulse" />
        <div className="h-16 bg-bw-surface-subtle rounded-lg animate-pulse" />
        <div className="h-16 bg-bw-surface-subtle rounded-lg animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-bw-surface rounded-lg p-4 border border-bw-border">
          <div className="text-2xl font-bold text-bw-ink">{templates.length}</div>
          <div className="text-xs text-bw-muted mt-0.5">Total proposals</div>
        </div>
        <div className="bg-bw-surface rounded-lg p-4 border border-bw-border">
          <div className="text-2xl font-bold text-bw-ink">{thisMonth}</div>
          <div className="text-xs text-bw-muted mt-0.5">Created this month</div>
        </div>
        <div className="bg-bw-surface rounded-lg p-4 border border-bw-border">
          <div className="text-2xl font-bold text-bw-ink">{groundedCount}</div>
          <div className="text-xs text-bw-muted mt-0.5">Source-grounded</div>
        </div>
      </div>

      {/* List */}
      <div className="bg-bw-surface rounded-lg border border-bw-border">
        <div className="p-6 border-b border-bw-border">
          <h3 className="text-lg font-semibold text-bw-ink flex items-center">
            <FileText className="h-5 w-5 mr-2 text-bw-muted" />
            Proposal Templates
          </h3>
          <p className="text-sm text-bw-muted mt-1">
            Open in the workspace to view, edit, recompute pricing, run the Red Team review, or export.
          </p>
        </div>

        <div className="p-6">
          {templates.length === 0 ? (
            <div className="text-center py-12 bg-bw-surface-subtle rounded-lg border-2 border-dashed border-bw-border">
              <FileText className="h-12 w-12 text-bw-muted mx-auto mb-3" />
              <p className="text-bw-ink font-medium">No proposals yet</p>
              <p className="text-sm text-bw-muted mt-1">
                Generate a proposal from a contract on your dashboard and it will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 p-4 bg-bw-surface-subtle rounded-lg border border-bw-border hover:border-servet-blue transition-colors"
                >
                  <button
                    onClick={() => handleView(template)}
                    className="flex items-center flex-1 min-w-0 text-left group"
                    title="Open in workspace"
                  >
                    <div className={`p-2 rounded-lg mr-3 shrink-0 ${
                      template.proposal_type === 'source-grounded' ? 'bg-bw-primary-tint text-bw-primary'
                        : template.proposal_type === 'enhanced' ? 'bg-bw-primary-tint text-bw-primary'
                        : 'bg-bw-surface text-bw-muted'
                    }`}>
                      {template.proposal_type === 'source-grounded' ? <Target className="h-5 w-5" />
                        : template.proposal_type === 'enhanced' ? <Zap className="h-5 w-5" />
                        : <FileText className="h-5 w-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-bw-ink truncate group-hover:text-bw-primary flex items-center gap-1.5">
                        {template.contract?.title || template.template_name}
                        <ExternalLink className="h-3.5 w-3.5 opacity-0 group-hover:opacity-60 shrink-0" />
                      </h4>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                        {template.contract?.agency && (
                          <span className="text-sm text-bw-muted truncate">{template.contract.agency}</span>
                        )}
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          template.proposal_type === 'source-grounded' ? 'bg-bw-primary-tint text-bw-primary'
                            : template.proposal_type === 'enhanced' ? 'bg-bw-primary-tint text-bw-primary'
                            : 'bg-bw-surface text-bw-ink'
                        }`}>
                          {template.proposal_type === 'source-grounded' ? 'Source-Grounded'
                            : template.proposal_type === 'enhanced' ? 'Enhanced' : 'Basic'}
                        </span>
                        <span className="text-xs text-bw-muted">
                          {new Date(template.updated_at || template.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </button>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => { setOpeningId(template.id); handleView(template); }}
                      className="flex items-center justify-center px-3 py-2 text-sm font-medium bg-bw-primary text-white rounded-md hover:brightness-110 transition"
                    >
                      {openingId === template.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />}
                      Open
                    </button>

                    <div className="relative">
                      <button
                        onClick={() => setShowExportMenu(showExportMenu === template.id ? null : template.id)}
                        disabled={exporting}
                        className="flex items-center justify-center px-3 py-2 text-sm font-medium border border-bw-border text-bw-ink rounded-md hover:bg-bw-surface transition disabled:opacity-50"
                      >
                        {exporting && showExportMenu === template.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
                        Export
                      </button>
                      {showExportMenu === template.id && (
                        <div className="absolute right-0 mt-2 w-44 bg-bw-surface rounded-md shadow-lg border border-bw-border z-10 py-1">
                          {(['pdf', 'word', 'sections', 'markdown'] as const).map((fmt) => (
                            <button
                              key={fmt}
                              onClick={() => handleExport(template, fmt)}
                              className="block w-full text-left px-4 py-2 text-sm text-bw-ink hover:bg-bw-surface-subtle capitalize"
                            >
                              {fmt === 'word' ? 'Word (.docx)' : fmt === 'pdf' ? 'PDF' : fmt === 'sections' ? 'Sections (.zip)' : 'Markdown'}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => handleRegenerate(template)}
                      className="flex items-center justify-center px-3 py-2 text-sm font-medium border border-bw-border text-bw-ink rounded-md hover:bg-bw-surface transition"
                      title="Re-run generation for this contract"
                    >
                      <RefreshCw className="h-4 w-4 mr-1.5" />
                      Regenerate
                    </button>

                    <button
                      onClick={() => handleRemove(template.id)}
                      className="flex items-center justify-center p-2 text-urgent border border-bw-border hover:border-red-400 rounded-md transition"
                      title="Delete proposal"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Regenerate via the modern orchestrated (source-grounded / checkpoint) generator */}
      {regenContract && (
        <OrchestratedProposalGenerator
          contract={regenContract as any}
          onClose={() => setRegenContract(null)}
          onSuccess={async () => {
            const noticeId = regenContract?.notice_id || regenContract?.id;
            setRegenContract(null);
            await loadTemplates();
            if (noticeId) openWorkspaceForNotice(noticeId);
          }}
        />
      )}

      {/* Click-away for the export menu */}
      {showExportMenu && (
        <div className="fixed inset-0 z-[5]" onClick={() => setShowExportMenu(null)} />
      )}
    </div>
  );
}
